import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { prefetchRecord, type CaseOverviewOut, type CaseStatus } from "@/api";
import { CaseCustomer, CaseStage, CaseVolume, CaseWhat, CaseWhen, caseTitleClass } from "@/components/CaseCells";
import { CaseSendWarnings, useReadAhead } from "@/components/SendWarnings";
import { StageControl } from "@/components/stages/StageControl";
import { formatMoney } from "@/lib/format";
import { setTurnDirection, usePageTurn } from "@/lib/motion";
import { canCancel, HANDOVER_CLOSED } from "@/lib/stages";

import styles from "./Register.module.css";
import { RowMenu, type RowMenuItem } from "./RowMenu";

/** A click on one of these belongs to the control, not to the row. */
const OWN_CLICK = "a, button, select, input, textarea, label";

/** How long the pointer rests on a row before its record is read ahead —
 * a sweep down the list asks the server nothing. */
const REST_MS = 100;

/** How long the focus rests in a row before its record is read ahead — Tab
 * down fifty rows asks the server nothing (final review, M-14). */
export const FOCUS_REST_MS = 150;

/**
 * Reads the record ahead once a row is about to be opened (design 2026-10-24
 * §7, `prefetchRecord`): the pointer resting on it, a press on it, or the
 * focus resting in it. The number and title fly into the record's head only
 * if the record is there when the page turns. A press reads at once; the
 * pointer and the focus only once they stay — the focus moving on within the
 * row (title, step, menu) waits on.
 */
function useOpenIntent(caseId: string) {
  const client = useQueryClient();
  const rest = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(rest.current), []);

  function readAhead() {
    window.clearTimeout(rest.current);
    prefetchRecord(client, caseId);
  }

  function readAfter(ms: number) {
    window.clearTimeout(rest.current);
    rest.current = window.setTimeout(readAhead, ms);
  }

  return {
    onPointerEnter: () => readAfter(REST_MS),
    onPointerLeave: () => window.clearTimeout(rest.current),
    onPointerDown: readAhead,
    onFocus: () => readAfter(FOCUS_REST_MS),
    onBlur: () => window.clearTimeout(rest.current),
  };
}

interface RegisterRowProps {
  item: CaseOverviewOut;
  busy: boolean;
  onStage: (id: string, to: CaseStatus) => void;
}

/**
 * One Vorgang, two lines per cell (UX ruling 4, F-2): what stands out on
 * the first, what qualifies it on the second — so every row is the same
 * height and the eye can run down a column. Long text ends in an ellipsis
 * with the whole of it in `title`.
 *
 * The whole row opens the record for the mouse; the title is a real link for
 * the keyboard and the screen reader (a focusable `<tr>` with buttons inside
 * would be a control inside a control). A click on a control of its own, or
 * while text is selected — an e-mail being copied —, opens nothing.
 * Explicit `role`s: the card layout below the intermediate width sets
 * `display: block`, and browsers drop a table's semantics with it (F-3).
 */
export function RegisterRow({ item, busy, onStage }: RegisterRowProps) {
  const navigate = useNavigate();
  // Where the focus lands once a stage gesture leaves this row nothing to
  // press — „Gewonnen" has no next step (fix round 3).
  const linkRef = useRef<HTMLAnchorElement>(null);
  // The step trigger names its Vorgang through this (fix round 4, M5).
  const titleId = useId();
  // „Angebot raus“ reads the Vorgang ahead on hover or focus (review final, 3).
  const readAhead = useReadAhead(item.id);
  const href = `/vorgang/${item.id}`;
  // Opening this Vorgang (or coming back from it), its name flies between this
  // row and the record's head (design 2026-10-24 §7): named only on the row
  // the page turns to, and only while it turns. One name for number and
  // title (UX-Gutachten 2, B-3) — the head's kicker and title as one piece;
  // the row's title is where that piece starts, tight round its words, and
  // the row's number goes with the list.
  const flying = usePageTurn(href);
  const openIntent = useOpenIntent(item.id);
  const volume = item.volume;
  // What the step's question names in place of an amount.
  const noAmount = volume === null ? "noch nicht kalkuliert" : "ohne Summe";
  const amountLabel = volume === null || volume.vkTotal === null ? noAmount : formatMoney(volume.vkTotal);
  // Words too long to share the chip's line on a card get a line of their
  // own there, so they are whole on a phone too: „freigegeben mit n Lücken"
  // under the chip (D-24, `extraWords`), and an open handover's recipient
  // (UX gate round 4, #6). Under the chip „Freigegeben" the release is only
  // „mit n Lücken" (#2) and fits beside it like any other word.
  const releaseWord =
    volume !== null && !volume.stale && volume.released && volume.releasedWithGaps && item.status !== "quote_approved";
  const wordsLine = releaseWord || item.pendingHandover !== null;

  // What a person does to a Vorgang from the list (UX gate round 4, #13;
  // ruling: no dialogs inside the register) — each item leads to the record
  // with the dialog asked for. „Übergeben …" only where a handover can go
  // out now: not in a finished stage (D-4), not while one waits.
  //
  // „Vorgang stornieren …" (spec 2026-10-15 §5) is such an item too: the menu
  // only knows links, so it leads to the record with the dialog asked for
  // (`?stornieren=1`) — one dialog, in the record, not a second one here.
  // Last, as the gesture with consequences. A cancelled Vorgang takes no
  // write at all — not a customer either, the record answers 409 — so its
  // row has no menu and „kein Kunde" is words, not a link.
  const cancelled = item.status === "cancelled";
  const assignHref = cancelled ? undefined : `${href}?kunde=1`;
  const menu: RowMenuItem[] = cancelled
    ? []
    : [
        { key: "customer", label: item.customer === null ? "Kunde zuordnen …" : "Kunde ändern …", to: `${href}?kunde=1` },
        ...(HANDOVER_CLOSED.has(item.status) || item.pendingHandover !== null
          ? []
          : [{ key: "handover", label: "Übergeben …", to: `${href}?uebergeben=1` }]),
        ...(canCancel(item.status) ? [{ key: "cancel", label: "Vorgang stornieren …", to: `${href}?stornieren=1` }] : []),
      ];

  function open(event: MouseEvent<HTMLTableRowElement>) {
    // Fix round 2, finding 2: anything portaled out of this row (the step's
    // popover) is still its React child, so its clicks bubble here. The
    // popover stops its own clicks at its root since round 3 (so the
    // record's head is covered too); this DOM-containment check stays as
    // the row's own guard for whatever else may ever portal out of it.
    if (event.target instanceof Element && !event.currentTarget.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest(OWN_CLICK)) return;
    // The whole step slot, not only its button (fix round 1, M1): a click
    // beside the gesture — or where it stood a moment ago, once „Gewonnen"
    // has left nothing to press — used to fall through to the row and open
    // the record, right next to a control that does something else entirely.
    // The slot is the chip's line from the chip to the cell's edge, on every
    // row (UX gate round 2, #8); the chip and its words open like any cell.
    if (event.target instanceof Element && event.target.closest('[data-row-exempt="step"]')) return;
    if ((window.getSelection()?.toString() ?? "") !== "") return;
    flyOpen();
    navigate(href);
  }

  // The page under the record's head waits until the name has flown past
  // the place it comes in at (base.css, `data-vt-richtung="flug"`); the
  // record's frame takes the word away once the page has turned.
  function flyOpen() {
    setTurnDirection("flug");
  }

  // The cells are the start page's too (UX gate round 2, #1): one grammar
  // for one Vorgang, laid out here as a table row, there as a link.
  return (
    <tr role="row" className={styles.row} onClick={open} {...openIntent} data-words-line={wordsLine ? "" : undefined}>
      <td role="cell" className={styles.cellCase}>
        <CaseWhat
          item={item}
          assignHref={assignHref}
          describedBy={titleId}
          title={
            <Link
              ref={linkRef}
              id={titleId}
              to={href}
              className={`${caseTitleClass} ${styles.open}`}
              title={item.title}
              style={flying ? { viewTransitionName: "vorgang-name" } : undefined}
              onClick={(event) => {
                // A press that opens the record in another tab or window
                // turns nothing here (the router's own test for such a click).
                if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
                flyOpen();
              }}
            >
              {item.title}
            </Link>
          }
        />
      </td>
      <td role="cell" className={styles.cellCustomer}>
        <CaseCustomer item={item} email assignHref={assignHref} describedBy={titleId} />
      </td>
      <td role="cell" className={styles.cellStage}>
        {/* The gesture beside its chip, in the stage's own cell (UX gate
            round 2, #8): it sat in a column of its own without a head, up to
            440 px from the stage it moves on. `.stage` lays the chip's line,
            the slot and the words under them out as one small grid. */}
        <div className={styles.stage}>
          <CaseStage item={item} />
          <div className={styles.step} data-row-exempt="step">
            <StageControl
              variant="action"
              caseId={item.id}
              status={item.status}
              busy={busy}
              onStage={onStage}
              caseNo={item.caseNo}
              amountLabel={amountLabel}
              returnFocusRef={linkRef}
              describedBy={titleId}
              // UX-Gutachten 4: what the mail asked for and the offer does
              // not hold — read ahead on hover or focus, shown in the question.
              sendWarnings={(id, onShown) => <CaseSendWarnings caseId={item.id} id={id} onShown={onShown} />}
              onSendIntent={readAhead}
            />
          </div>
        </div>
      </td>
      <td role="cell" className={`${styles.cellVolume} ${styles.right}`}>
        {/* A dash under the head „Volumen"; the words on a card, where the
            head is gone (R2-12, UX gate round 2, #1) — the register's cards
            swap the two in their own tier. */}
        <CaseVolume item={item} missing="both" />
      </td>
      <td role="cell" className={styles.cellWhen}>
        {/* The row's menu at the end of the moment's line (UX gate round 4,
            #13): the last cell, where a row's own actions are looked for,
            without a column of its own — a head without a word is what
            round 2 (#8) took away. */}
        <div className={styles.when}>
          <CaseWhen item={item} />
          <div className={styles.whenMenu}>
            {menu.length > 0 ? <RowMenu items={menu} describedBy={titleId} /> : null}
          </div>
        </div>
      </td>
    </tr>
  );
}
